import React from 'react';
import { ArrowLeft } from 'lucide-react';
import Seo from './components/Seo';

const sections = [
  {
    title: '1. Scope and Applicability',
    paragraphs: [
      'This Privacy Policy describes how Jobisa collects, uses, stores, shares, and protects personal data when you use the Jobisa Worker app, the Jobisa Recruiter app, the Jobisa website, public worker profiles, in-app messaging features, recruiter tools, and Jobisa-operated WhatsApp onboarding or support channels.',
      'This Policy is intended to be read together with our Terms and Conditions. By creating an account, submitting information, or continuing to use the Platform, you acknowledge that your personal data may be processed in the manner described here, subject to your settings, permissions, and rights under applicable law.',
    ],
  },
  {
    title: '2. Indian Law Framework',
    paragraphs: [
      'Jobisa is operated for users in India and is designed with reference to applicable Indian law, including the Digital Personal Data Protection Act, 2023 (DPDPA), the Information Technology Act, 2000, and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, to the extent applicable to our services and data handling practices.',
      'Where we rely on your consent, we will request it through the app, device permissions, forms, uploads, or other clear user actions. We may also process personal data for service delivery, fraud prevention, security, legal compliance, grievance handling, and other uses permitted by applicable law.',
    ],
  },
  {
    title: '3. Information We Collect',
    paragraphs: [
      'The exact data we collect depends on whether you are a worker, recruiter, website visitor, or a user interacting with Jobisa over WhatsApp or support channels.',
    ],
    bullets: [
      'Identity and contact information, such as your name, phone number, email address, city, company details, and account credentials.',
      'Worker profile information, such as profession, skills, work preferences, experience, education, language preference, passport availability, profile photo, resume details, and public profile information you choose to display.',
      'Recruiter profile and business information, such as recruiter name, company name, branding assets, hiring preferences, campaign content, job descriptions, interview details, and recruiter wallet or token transaction history.',
      'Documents and media you upload, including profile photos, government or travel documents, PDFs, certificates, chat attachments, video CVs, voice notes, audio samples, and related metadata.',
      'Video and Audio Recordings: With your explicit permission, Jobisa may record and store video CVs (up to 90 seconds) and voice CV recordings submitted through the Jobisa Worker app. These recordings are a core feature of the platform, used to showcase your professional introduction to potential employers. Video CVs may include your face, voice, and spoken summary of your skills and experience. You may delete these recordings at any time from your profile.',
      'Location information where you grant permission, including precise or approximate location data used to show nearby jobs, local opportunities, or location-aware job details.',
      'Communications data, including messages between workers and recruiters, support chats, interview updates, notification preferences, WhatsApp messages, and media shared through Jobisa channels.',
      'Device, technical, and usage information, such as app version, device identifiers, push notification tokens, operating system details, crash or diagnostic data, and interaction history needed for product reliability and abuse prevention.',
      'AI-assisted interaction data, including transcripts, extracted profile fields, prompts, responses, and quality or safety logs when you use voice CV, Safal AI, or similar assistant features.',
    ],
  },
  {
    title: '4. Device Permissions and Access',
    paragraphs: [
      'The Jobisa Worker app may request access to location, camera, microphone, photo library or storage, notifications, and speech-related features so workers can discover nearby jobs, upload documents, record voice or video profiles, and receive operational alerts. Specifically, camera and microphone access are required to record Video CVs and Voice CVs, which are a core part of your worker profile.',
      'The Jobisa Recruiter app may request access to images, videos, audio, storage or media library, and notifications so recruiters can create hiring content, upload media, and receive platform alerts. Device permissions can usually be revoked from your phone settings, but some features may stop functioning correctly if the relevant permission is disabled.',
    ],
  },
  {
    title: '5. How We Use Personal Data',
    bullets: [
      'To create and manage accounts, profiles, logins, and onboarding flows.',
      'To match workers and recruiters based on profile details, work preferences, skills, job type, and application history.',
      'To host and display job listings, campaigns, public worker profiles, recruiter materials, and interview workflows.',
      'To process user-submitted documents, audio, video, and messaging content, including moderation, verification, and profile extraction workflows.',
      'To process, store, and display Video CVs and Voice CVs to potential recruiters as part of the hiring workflow.',
      'To send service notifications, interview alerts, security notices, product updates, and WhatsApp or in-app onboarding messages.',
      'To improve product performance, detect abuse, prevent fraud, enforce platform rules, investigate complaints, and maintain platform integrity.',
      'To administer recruiter wallet balances, token transactions, pricing rules, billing records, support operations, and internal analytics.',
      'To comply with legal obligations, respond to valid law-enforcement requests, and establish, exercise, or defend legal claims.',
    ],
  },
  {
    title: '6. Sharing and Disclosure',
    paragraphs: [
      'We do not sell personal data. We may share information only where reasonably necessary for the operation of the Platform or where required by law.',
    ],
    bullets: [
      'With recruiters, when workers choose to apply, create a public profile, or otherwise make profile details available for hiring-related purposes. This includes sharing Video CVs, Voice CVs, profile photos, and other media with verified recruiters reviewing candidates.',
      'Worker data will only be shared with verified recruiters for the purpose of job matching and will not be sold to third-party marketing agencies.',
      'With workers, when recruiters publish jobs, company details, hiring materials, interview schedules, or recruiter contact details through the Platform.',
      'With service providers who support hosting, storage, notifications, communications, analytics, fraud prevention, document handling, AI-enabled processing, or customer support, subject to appropriate contractual or technical controls.',
      'With Meta or WhatsApp infrastructure, where you interact with Jobisa through WhatsApp flows, templates, or support conversations.',
      'With legal authorities, regulators, courts, or enforcement agencies where disclosure is required by law, lawful process, or to protect rights, safety, property, or platform security.',
      'In connection with a business transfer, financing, restructuring, or merger, subject to confidentiality and lawful handling requirements.',
    ],
  },
  {
    title: '7. Public Profiles and User Content',
    paragraphs: [
      'If a worker enables or is provided a public profile, certain profile information may become visible on the web to facilitate hiring. Workers should avoid including unnecessary sensitive personal information in public-facing profile fields.',
      'Recruiters and workers remain responsible for the accuracy, legality, and appropriateness of the content they upload or share on the Platform. We may moderate, restrict, or remove content that violates our policies, legal obligations, or platform standards.',
    ],
  },
  {
    title: '8. WhatsApp and Unregistered Leads',
    paragraphs: [
      'If you contact Jobisa through WhatsApp, we may store your phone number, language preference, messages, voice notes, videos, extracted profile details, and review status. This may include unregistered lead data before you create a full app account.',
      'If you later register on the Jobisa Worker app using the same phone number, Jobisa may use previously collected onboarding information to prefill or simplify your registration flow, subject to review and correction.',
      'By messaging the Jobisa WhatsApp bot, you explicitly opt-in to receive job alerts, platform updates, and onboarding messages via WhatsApp. You may opt-out of all WhatsApp messages at any time by typing "STOP" in the chat. Opting out will stop all future messages but will not delete your existing account or data.',
    ],
  },
  {
    title: '9. Retention',
    paragraphs: [
      'We retain personal data only for as long as reasonably necessary for the purposes described in this Policy, including account management, hiring workflows, fraud prevention, compliance, dispute handling, and record-keeping.',
      'Retention periods may vary based on whether you remain an active user, whether content is subject to moderation or disputes, and whether we must preserve records for legal, tax, audit, or security reasons. When data is no longer required, we will delete, anonymize, or de-identify it where reasonably practicable.',
    ],
  },
  {
    title: '10. Security',
    paragraphs: [
      'Jobisa uses technical and organizational measures intended to protect personal data against unauthorized access, disclosure, alteration, loss, or misuse. These measures may include authenticated access controls, encryption in transit, storage protections, logging, and administrative review controls.',
      'No system can guarantee absolute security. Users are responsible for maintaining the confidentiality of their account credentials, using trusted devices, and notifying us promptly if they suspect unauthorized access or misuse.',
    ],
  },
  {
    title: '11. Cross-Border Processing',
    paragraphs: [
      'Some of our infrastructure or service providers may process data outside India. Where cross-border processing occurs, we will take commercially reasonable steps to ensure the processing remains consistent with applicable law and this Policy.',
    ],
  },
  {
    title: '12. Your Rights and Choices',
    bullets: [
      'You may review and update certain account details directly in the app or by contacting support.',
      'You may request correction or deletion of inaccurate or no-longer-needed data, subject to applicable law, platform safety needs, and lawful retention requirements.',
      'You may revoke optional device permissions from your device settings, although affected features may become unavailable.',
      'You may opt out of certain promotional communications, though service and security communications may still be sent where necessary.',
      'You may request complete deletion of your account and all associated data (including Video CVs, Voice CVs, and uploaded media) by emailing hiring@jobisa.in from your registered email or by messaging "DELETE MY DATA" on WhatsApp. See our Data Deletion page at jobisa.in/delete-data for full instructions.',
      'You may raise a grievance or privacy request by writing to hiring@jobisa.in. We may ask you to verify your identity before acting on a request.',
    ],
  },
  {
    title: '13. Children',
    paragraphs: [
      'Jobisa is not intended for children. Users must not create accounts or submit personal data through the Platform unless they are legally permitted to do so under applicable law and platform rules. Where we become aware that personal data has been collected inappropriately, we may remove or restrict the relevant account or content.',
    ],
  },
  {
    title: '14. Grievances and Contact',
    paragraphs: [
      'For privacy questions, data correction requests, account deletion requests, or grievance-related matters, please contact Jobisa at hiring@jobisa.in. If you contact us, please include sufficient information to help us identify your account and process your request securely.',
    ],
  },
  {
    title: '15. Changes to this Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time to reflect product changes, legal developments, or operational requirements. The latest version will be posted on the website and may also be made available in-app. Your continued use of the Platform after an update becomes effective will be subject to the revised Policy.',
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-sans py-12">
      <Seo
        title="Privacy Policy"
        description="Jobisa Privacy Policy for worker and recruiter apps, website services, messaging, media uploads, video CVs, voice recordings, location permissions, AI features, and WhatsApp onboarding."
        path="/privacy-policy"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Jobisa Privacy Policy',
          url: 'https://jobisa.in/privacy-policy',
          description: 'Privacy Policy covering Jobisa worker and recruiter products and related services.',
        }}
      />
      <div className="container mx-auto px-6 lg:px-12 max-w-4xl bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-gray-100">
        <a href="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 font-medium">
          <ArrowLeft size={20} className="mr-2" /> Back to Home
        </a>

        <h1 className="text-4xl font-bold font-display text-jobisa-navy mb-4">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: March 24, 2026</p>

        <div className="space-y-8 leading-relaxed text-gray-700">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-2xl font-bold font-display text-jobisa-navy mb-4">{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="mb-3">
                  {paragraph}
                </p>
              ))}
              {section.bullets && (
                <ul className="list-disc pl-6 space-y-2">
                  {section.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 40)}>{bullet}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
